import { Alert, message, Spin, Table, Typography } from "antd"
import { memo, useEffect, useMemo, useState } from "react"
import { useActions } from "../../../hooks/useActions"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { TABLE_SCROLL_X } from "../constants/columns.constants"
import {
  buildBidAdjustmentItem,
  buildPlacementRowsForTable,
  computeMaxTargetRaiseRatio,
  computeBaseBid,
  SB_AMAZON_MIN_BID,
  computeCurrentBid,
  computeLiveWeightedBid,
  computeSpendWeightedModifier,
  countBaseBidTargets,
  hasAudienceChange,
  hasModifierChanges,
  isBaseAboveLiveSizing,
  resolveAudienceId,
  resolveDeflationContext,
} from "../hooks/helpers/sbPlacementModifier.helper"
import { useSbPlacementsColumns } from "../hooks/useSbPlacementsColumns"
import { ISbPlacementRowForTable } from "../types/sbPlacement.types"
import styles from "./SponsoredBrandsPlacementsTable.module.scss"

interface ISponsoredBrandsPlacementsTable {
  campaignId: string
}

const SponsoredBrandsPlacementsTableComponent: React.FC<ISponsoredBrandsPlacementsTable> = ({
  campaignId,
}) => {
  const { placementData, pendingAdjustments, loading, error } = useTypedSelector(
    (select) => select.sponsoredBrandsPlacements
  )
  const { targetDataForTable } = useTypedSelector((select) => select.sponsoredBrandsTargetForTable)
  const pendingAudienceId = useTypedSelector(
    (select) =>
      select.sponsoredBrandsPlacements.pendingAdjustments[campaignId]?.audiences?.[0]?.audienceId
  )
  const maxTargetCPC = useTypedSelector(
    (select) => select.sponsoredBrands.campaignData.maxTargetCPC
  )
  const thinTOS = useTypedSelector((select) => select.sponsoredBrands.campaignData.TOSPercentage)
  const thinAD = useTypedSelector((select) => select.sponsoredBrands.campaignData.ADPercentage)
  const thinDataDefaults = useMemo(
    () => ({ TOSPercentage: thinTOS, ADPercentage: thinAD }),
    [thinTOS, thinAD]
  )
  const { setSbPlacementAdjustment } = useActions()

  const [rows, setRows] = useState<ISbPlacementRowForTable[]>([])
  const [audienceId, setAudienceId] = useState<string | null>(null)

  const campaignPlacements = placementData.find(
    (data) => String(data.campaignId) === String(campaignId)
  )
  const loadedTargets = targetDataForTable.find(
    (data) => String(data.campaignId) === String(campaignId)
  )
  const targetsLoaded = loadedTargets !== undefined
  const campaignTargets = loadedTargets?.targetData ?? []
  const deflationContext = resolveDeflationContext(campaignId, pendingAdjustments, placementData)
  // [NEW-BASE-04] Once the campaign's targets are loaded the base is ONLY the spend-weighted final
  // bid of its enabled targets (null = no valid final bid -> suggestions keep their live %). The
  // backend's live-bid average is a LOADING placeholder only, and is labelled as such.
  const computedBaseBid = targetsLoaded
    ? computeBaseBid(campaignTargets, deflationContext, SB_AMAZON_MIN_BID, maxTargetCPC)
    : null
  const baseBid = targetsLoaded ? computedBaseBid : (campaignPlacements?.baseBid ?? null)
  const baseSource: BaseSource = targetsLoaded ? "final-bids" : "live-placeholder"
  const currentBid = campaignPlacements?.currentBid ?? computeCurrentBid(campaignTargets)
  const enabledTargetCount = countBaseBidTargets(campaignTargets)
  const liveWeightedBid =
    computeLiveWeightedBid(campaignTargets) ?? campaignPlacements?.baseBid ?? null
  const liveModifier = computeSpendWeightedModifier({ ...deflationContext, pending: null })
  // A7: per-target raise ratio so a thin-data placement holds for EVERY target (not the average)
  const maxTargetRaiseRatio = targetsLoaded
    ? computeMaxTargetRaiseRatio(campaignTargets, deflationContext, SB_AMAZON_MIN_BID, maxTargetCPC)
    : null
  const showLiveSizingNote = isBaseAboveLiveSizing(baseBid, liveWeightedBid, liveModifier)

  function recompute(nextRows: ISbPlacementRowForTable[]): ISbPlacementRowForTable[] {
    if (!campaignPlacements) {
      return nextRows
    }

    return buildPlacementRowsForTable(
      nextRows,
      campaignPlacements.bidding,
      baseBid,
      currentBid,
      liveWeightedBid,
      maxTargetRaiseRatio,
      thinDataDefaults
    )
  }

  useEffect(() => {
    if (!campaignPlacements) {
      return
    }

    setRows(
      buildPlacementRowsForTable(
        campaignPlacements.placements,
        campaignPlacements.bidding,
        baseBid,
        currentBid,
        liveWeightedBid,
        maxTargetRaiseRatio,
        thinDataDefaults
      )
    )
    setAudienceId(resolveAudienceId(campaignPlacements.bidding))
  }, [
    campaignPlacements,
    baseBid,
    currentBid,
    liveWeightedBid,
    maxTargetRaiseRatio,
    thinDataDefaults,
  ])

  useEffect(() => {
    if (!pendingAudienceId) {
      return
    }

    setAudienceId((prev) => (prev === pendingAudienceId ? prev : pendingAudienceId))
  }, [pendingAudienceId])

  useEffect(() => {
    if (!campaignPlacements) {
      return
    }

    const { bidding } = campaignPlacements
    const audienceChanged = hasAudienceChange(bidding, audienceId)
    const changed = hasModifierChanges(rows, bidding) || audienceChanged

    setSbPlacementAdjustment(
      campaignId,
      changed
        ? buildBidAdjustmentItem(campaignId, rows, bidding, audienceChanged ? audienceId : null)
        : null
    )
  }, [rows, audienceId])

  useEffect(() => {
    if (error) {
      message.error("Failed to load placements")
    }
  }, [error])

  function changeModifier(placement: string, value: number) {
    setRows((prev) =>
      prev.map((row) => (row.placement === placement ? { ...row, modifier: value } : row))
    )
  }

  function changeTargetAcos(placement: string, value: number) {
    setRows((prev) =>
      recompute(
        prev.map((row) => (row.placement === placement ? { ...row, targetAcos: value } : row))
      )
    )
  }

  function changeAudienceId(value: string) {
    setAudienceId(value)
    setRows((prev) => recompute(prev))
  }

  const columns = useSbPlacementsColumns(
    changeModifier,
    changeTargetAcos,
    changeAudienceId,
    audienceId
  )

  if (!campaignPlacements) {
    return <Spin />
  }

  return (
    <div className={styles.placementsBlock}>
      <Typography.Text type="secondary">
        {formatBaseBidLabel(baseBid, enabledTargetCount, baseSource)}
      </Typography.Text>
      {showLiveSizingNote && <Alert type="warning" showIcon={true} message={LIVE_SIZING_NOTE} />}
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="placement"
        pagination={false}
        loading={loading}
        tableLayout="fixed"
        scroll={{ x: TABLE_SCROLL_X }}
      />
    </div>
  )
}

const LIVE_SIZING_NOTE =
  "Live modifiers were sized for a lower base — apply placements and bids together"

type BaseSource = "final-bids" | "live-placeholder"

export function formatBaseBidLabel(
  baseBid: number | null,
  enabledTargetCount: number,
  source: BaseSource = "final-bids"
): string {
  const base = baseBid === null ? "-" : `$${baseBid.toFixed(2)}`
  if (source === "live-placeholder") {
    return `Base ${base} (live bids — targets not loaded yet, final-bid base pending)`
  }
  const targets = enabledTargetCount === 1 ? "target" : "targets"
  if (baseBid === null) {
    return `Base - (no valid final bid among ${enabledTargetCount} enabled ${targets} — modifiers keep their live %)`
  }
  return `Base ${base} (spend-weighted final bid of ${enabledTargetCount} enabled ${targets})`
}

export const SponsoredBrandsPlacementsTable = memo(SponsoredBrandsPlacementsTableComponent)
